<!-- User-Login.vue -->
<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { useRouter, useRoute, RouterLink } from 'vue-router';
import { 
  X, 
  User as UserIcon, 
  Lock, 
  Phone, 
  Mail, 
  Smartphone, 
  ShieldCheck, 
  ChevronRight,
  ShieldAlert,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Ticket
} from 'lucide-vue-next';
import { authStore } from '@/authStore.js';

const TRY_CACHE_KEY = 'detail_try_cache';
const router = useRouter();
const route = useRoute();

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'open', 'loginSuccess']);

const mode = ref('login');
const showLoginPassword = ref(false);
const loginMethod = ref('password'); 
const recoverySent = ref(false);
const resetSuccess = ref(false);
const statusMessage = ref('');
const isError = ref(false);
const showPassword = ref(false);
const enableCodeLogin = ref(false);
// 协议勾选变量
const agreePolicy = ref(false);

// 登录锁定相关变量（全部移到函数外部）
const isLoggingIn = ref(false);
const loginAttempts = ref(0);
const isLocked = ref(false);
const lockTimer = ref(null);

const loginData = reactive({
  account: '',
  password: '',
  phone: '',
  code: ''
});

const registerData = reactive({
  username: '',
  password: '',
  email: '',
  phone: '',
  invite_code: ''
});

const forgotData = reactive({
  email: ''
});

const resetData = reactive({
  uid: '',
  token: '',
  new_password: '',
  re_new_password: ''
});

// Validation states
const errors = reactive({
  username: '',
  password: '',
  email: '',
  phone: '',
  forgotEmail: '',
  resetPassword: '',
  resetRePassword: ''
});

const showMessage = (msg, error = false) => {
  isSendingCode.value = false;
  countdown.value = 0;
  
  statusMessage.value = msg;
  isError.value = error;
  setTimeout(() => {
    statusMessage.value = '';
  }, 1000);
};

onMounted(async () => {
  if (route.params.uid && route.params.token && route.path.includes('password_reset')) {
    resetData.uid = route.params.uid;
    resetData.token = route.params.token;
    mode.value = 'reset';
    if (!props.show) {
      emit('open');
    }
  }

  if (route.query.invite_code) {
    registerData.invite_code = route.query.invite_code;
  }

  if (!authStore.isLoggedIn && localStorage.getItem('refresh')) {
    const success = await authStore.refreshToken();
    if (success) {
      showMessage('欢迎回来，已自动恢复登录');
      setTimeout(() => {
        emit('close');
        router.push('/detail');
      }, 1000);
    }
  }
});

watch(() => route.params, (newParams) => {
  if (newParams.uid && newParams.token && route.path.includes('password_reset')) {
    resetData.uid = newParams.uid;
    resetData.token = newParams.token;
    mode.value = 'reset';
    if (!props.show) emit('open');
  }
}, { deep: true });

const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const validatePassword = (password) => {
  const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
  return regex.test(password);
};

const loginIdentifierIcon = computed(() => {
  if (!loginData.account) return UserIcon;
  if (validateEmail(loginData.account)) return Mail;
  if (/^\d{7,15}$/.test(loginData.account.replace(/\s+/g, ''))) return Phone;
  return UserIcon;
});

const checkUsername = () => {
  if (!registerData.username) {
    errors.username = '请输入账号名称';
  } else {
    errors.username = '';
  }
};

const checkPassword = () => {
  if (!registerData.password) {
    errors.password = '请设置密码';
  } else if (!validatePassword(registerData.password)) {
    errors.password = '密码需至少8位，且包含字母和数字';
  } else {
    errors.password = '';
  }
};

const checkEmail = () => {
  if (!registerData.email) {
    errors.email = '邮箱地址为必填项，用于找回密码';
  } else if (!validateEmail(registerData.email)) {
    errors.email = '请输入有效的邮箱格式';
  } else {
    errors.email = '';
  }
};

const checkPhone = () => {
  if (!registerData.phone) {
    errors.phone = '请输入手机号';
  } else if (!/^\d{11}$/.test(registerData.phone)) {
    errors.phone = '请输入有效的11位手机号码';
  } else {
    errors.phone = '';
  }
};

const checkForgotEmail = () => {
  if (!forgotData.email) {
    errors.forgotEmail = '请输入注册时的邮箱地址';
  } else if (!validateEmail(forgotData.email)) {
    errors.forgotEmail = '请输入有效的邮箱格式';
  } else {
    errors.forgotEmail = '';
  }
};

const checkResetPassword = () => {
  if (!resetData.new_password) {
    errors.resetPassword = '请输入新密码';
  } else if (!validatePassword(resetData.new_password)) {
    errors.resetPassword = '密码需至少8位，且包含字母和数字';
  } else {
    errors.resetPassword = '';
  }
};

const checkResetRePassword = () => {
  if (!resetData.re_new_password) {
    errors.resetRePassword = '请再次输入密码';
  } else if (resetData.re_new_password !== resetData.new_password) {
    errors.resetRePassword = '两次输入的密码不一致';
  } else {
    errors.resetRePassword = '';
  }
};

const isSendingCode = ref(false);
const countdown = ref(0);

const startCountdown = () => {
  if (countdown.value > 0) return;
  isSendingCode.value = true;
  countdown.value = 60;
  const timer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(timer);
      isSendingCode.value = false;
    }
  }, 1000);
};

const handleLogin = async () => {
  if (isLocked.value) {
    showMessage('请等待锁定解除后再试', true);
    return;
  }
  if (isLoggingIn.value) return;
  
  statusMessage.value = '';
  isError.value = false;
  isLoggingIn.value = true;
  
  try {
    if (loginMethod.value === 'password') {
      const payload = { 
        username: loginData.account, 
        password: loginData.password 
      };
      
      const response = await axios.post('/api/membership/jwt/create/', payload);
      
      loginAttempts.value = 0;
      const { refresh, access, user_info } = response.data;
      
      localStorage.setItem('token', access);
      localStorage.setItem('refresh', refresh);
      
      if (user_info) {
        localStorage.setItem('username', user_info.username);
        authStore.login(user_info);
      } else {
        authStore.login({ username: loginData.account });
      }

      // 新增：登录成功立刻同步试用资格缓存
      const token = localStorage.getItem('token');
      if(token) {
        axios.get('/api/z_model/check-detail-try/', {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }).then(res => {
          if (res.data.code === 0) {
            const data = res.data.data;
            localStorage.setItem(TRY_CACHE_KEY, JSON.stringify(data));
          }
        }).catch(()=>{})
      }
      
      showMessage('登录成功!');
      setTimeout(() => {
        emit('loginSuccess'); // 新增
        router.push('/detail');
      }, 1000);
    }
  } catch (error) {
    loginAttempts.value++;
    console.error('登录失败次数:', loginAttempts.value);
    
    if (loginAttempts.value >= 3) {
      isLocked.value = true;
      const waitTime = Math.min(loginAttempts.value * 5, 30);
      
      showMessage(`登录失败次数过多，请${waitTime}秒后再试`, true);
      
      if (lockTimer.value) clearTimeout(lockTimer.value);
      
      lockTimer.value = setTimeout(() => {
        isLocked.value = false;
        loginAttempts.value = 0;
        statusMessage.value = '';
        console.log('锁定已解除');
      }, waitTime * 1000);
    } else {
      showMessage('登入失败！请确认您的账户和密码', true);
    }
  } finally {
    isLoggingIn.value = false;
  }
};

const handleRegister = () => {
  checkUsername();
  checkPassword();
  checkEmail();
  checkPhone();

  // 协议勾选校验
  if (!agreePolicy.value) {
    showMessage('请阅读并同意服务协议和隐私协议', true);
    return;
  }

  if (errors.username || errors.password || errors.email || errors.phone) {
    return;
  }

  const formData = {
    'username': registerData.username,
    'password': registerData.password,
    'email': registerData.email,
    'phone': registerData.phone
  }

  if (registerData.invite_code) {
    formData.invite_code = registerData.invite_code;
  }

  axios
    .post('/api/membership/users/', formData)
    .then(() => {
        showMessage('用户注册成功！请登录。');
        registerData.username = '';
        registerData.password = '';
        registerData.email = '';
        registerData.phone = '';
        registerData.invite_code = '';
        
        setTimeout(() => {
          mode.value = 'login';
          loginMethod.value = 'password';
        }, 1500);
    })
    .catch(error => {
        console.error('Error creating user:', error);
        let errorMsg = '注册失败';
        if (error.response?.data) {
          const data = error.response.data;
          if (typeof data === 'string') {
            errorMsg = data;
          } else if (typeof data === 'object') {
            const errorValues = Object.values(data);
            if (errorValues.length > 0) {
              const firstError = errorValues[0];
              errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
            }
          }
        } else {
          errorMsg = '网络连接异常，请稍后重试';
        }
        showMessage(errorMsg, true);
    });
};

const handleForgot = () => {
  checkForgotEmail();
  if (errors.forgotEmail) return;
  
  axios.post('/api/membership/users/reset_password/', { email: forgotData.email })
    .then(() => {
      recoverySent.value = true;
      setTimeout(() => {
        recoverySent.value = false;
        mode.value = 'login';
        loginMethod.value = 'password';
      }, 3000);
    })
    .catch(error => {
      showMessage(error.response?.data?.detail || '邮件发送失败', true);
    });
};

const handleResetPassword = () => {
  checkResetPassword();
  checkResetRePassword();

  if (errors.resetPassword || errors.resetRePassword) return;

  const payload = {
    uid: resetData.uid,
    token: resetData.token,
    new_password: resetData.new_password,
    re_new_password: resetData.re_new_password
  };

  axios.post('/api/membership/users/reset_password_confirm/', payload)
    .then(() => {
      resetSuccess.value = true;
      showMessage('密码重置成功！');
      setTimeout(() => {
        resetSuccess.value = false;
        mode.value = 'login';
        loginMethod.value = 'password';
        router.replace('/');
      }, 3000);
    })
    .catch(error => {
      console.error('Reset password error:', error);
      let errorMsg = '重置失败，链接可能已过期或无效';
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === 'object') {
          const errorValues = Object.values(data);
          if (errorValues.length > 0) {
            const firstError = errorValues[0];
            errorMsg = Array.isArray(firstError) ? firstError[0] : (typeof firstError === 'string' ? firstError : JSON.stringify(firstError));
          }
        }
      }
      showMessage(errorMsg, true);
    });
};

const close = () => {
  // 不再只emit，同步关闭全局状态
  authStore.closeLoginModal();
  emit('close');
};
</script>

<template>
  <transition name="modal-fade">
    <div v-if="show" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="close"></div>
      
      <div class="relative w-full max-w-[840px] h-[520px] bg-[#1c1c1e] rounded-[32px] overflow-hidden flex shadow-2xl border border-white/5 animate-scale-in">
        
        <button @click="close" class="absolute top-6 right-6 z-50 p-2 text-gray-500 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full">
          <X :size="20" />
        </button>

        <div class="hidden md:flex w-[340px] shrink-0 relative flex-col items-center justify-center p-8 overflow-hidden bg-black border-r border-white/5">
           <div class="absolute inset-0 z-0">
              <div class="blob-1 absolute top-[-10%] left-[-10%] w-[120%] h-[120%] bg-indigo-600/20 blur-[80px] rounded-full animate-flow-1"></div>
              <div class="blob-2 absolute bottom-[-10%] right-[-10%] w-[120%] h-[120%] bg-emerald-500/20 blur-[80px] rounded-full animate-flow-2"></div>
              <div class="blob-3 absolute top-1/4 right-1/4 w-[100%] h-[100%] bg-amber-500/10 blur-[60px] rounded-full animate-flow-3"></div>
           </div>

           <div class="relative z-10 text-center px-4">
              <div class="w-16 h-16 bg-[#00d6b9] rounded-2xl flex items-center justify-center text-black font-black text-3xl shadow-[0_0_30px_rgba(0,214,185,0.3)] mx-auto mb-6">D</div>
              <h2 class="text-3xl font-black text-white mb-3 tracking-tight">DIKO &middot; AI</h2>
              <p class="text-gray-400 text-sm font-medium leading-relaxed">让电商设计更简单</p>
           </div>
        </div>

        <div class="flex-1 p-10 flex flex-col justify-center relative bg-gradient-to-br from-[#1c1c1e] to-[#0b0d10]">
          <div class="w-full max-sm mx-auto">
             <transition name="fade-in">
               <div v-if="statusMessage" 
                 class="mb-4 p-3 rounded-xl text-xs flex items-center gap-2 animate-fade-in"
                 :class="isError ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'"
               >
                 <component :is="isError ? AlertCircle : CheckCircle2" :size="14" />
                 <span class="truncate-2-lines">{{ statusMessage }}</span>
               </div>
             </transition>

             <div v-if="mode !== 'forgot' && mode !== 'reset'" class="flex gap-6 mb-8 border-b border-white/5">
                <button @click="mode = 'login'" class="pb-3 text-lg font-bold transition-all relative" :class="mode === 'login' ? 'text-white' : 'text-gray-500 hover:text-gray-300'">
                  登录
                  <div v-if="mode === 'login'" class="absolute bottom-0 left-0 w-full h-1 bg-[#00d6b9] rounded-full"></div>
                </button>
                <button @click="mode = 'register'" class="pb-3 text-lg font-bold transition-all relative" :class="mode === 'register' ? 'text-white' : 'text-gray-500 hover:text-gray-300'">
                  注册
                  <div v-if="mode === 'register'" class="absolute bottom-0 left-0 w-full h-1 bg-[#00d6b9] rounded-full"></div>
                </button>
             </div>

             <transition name="fade-slide" mode="out-in">
               <div v-if="mode === 'login'" key="login" class="space-y-6">
                  <h3 class="text-center text-white text-xl font-bold mb-6">密码登录</h3>

                  <!-- 验证码登录界面 完整保留，仅隐藏 -->
                  <div v-if="loginMethod === 'code' && enableCodeLogin" class="space-y-4 animate-fade-in">
                    <div class="relative">
                      <Phone :size="18" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input v-model="loginData.phone" type="tel" placeholder="请输入手机号" class="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-sm focus:border-[#00d6b9] transition-all outline-none" />
                    </div>
                    <div class="flex gap-3">
                      <div class="relative flex-1">
                        <ShieldAlert :size="18" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input v-model="loginData.code" type="text" placeholder="短信验证码" class="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-sm focus:border-[#00d6b9] transition-all outline-none" />
                      </div>
                      <button @click="startCountdown" :disabled="countdown > 0" class="px-4 rounded-xl text-[10px] font-bold border border-white/10 bg-white/5 hover:bg-white/10 transition-colors disabled:text-gray-600">
                        {{ countdown > 0 ? `${countdown}s` : '获取' }}
                      </button>
                    </div>
                    <button @click="handleLogin" class="w-full py-4 bg-[#00d6b9] hover:bg-[#00c4a8] text-[#0b0d10] font-bold rounded-2xl transition-all shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-2 mt-2">
                       <span>立即进入</span> <ChevronRight :size="18" />
                    </button>
                    <button @click="loginMethod = 'password'" class="w-full py-2 text-xs text-gray-500 hover:text-gray-300 transition-colors">返回密码登录</button>
                  </div>

                  <!-- 默认密码登录 -->
                  <div v-if="loginMethod === 'password'" class="space-y-4 animate-fade-in">
                    <div class="relative">
                      <component :is="loginIdentifierIcon" :size="18" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 transition-all duration-300" />
                      <input v-model="loginData.account" type="text" placeholder="用户名 / 手机号 / 邮箱" class="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-sm focus:border-[#00d6b9] transition-all outline-none" />
                    </div>
                    <div class="relative">
                      <Lock :size="18" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input 
                        v-model="loginData.password" 
                        :type="showLoginPassword ? 'text' : 'password'" 
                        placeholder="请输入密码" 
                        class="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-10 text-sm focus:border-[#00d6b9] transition-all outline-none" 
                      />
                      <button 
                        @click="showLoginPassword = !showLoginPassword"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        <component :is="showLoginPassword ? EyeOff : Eye" :size="16" />
                      </button>
                    </div>
                    <div class="flex justify-end">
                       <button @click="mode = 'forgot'" class="text-[10px] text-gray-500 hover:text-[#00d6b9] transition-colors">忘记密码？</button>
                    </div>
                    <button 
                      @click="handleLogin" 
                      :disabled="isLoggingIn || isLocked"
                      :class="(isLoggingIn || isLocked) ? 'opacity-50 cursor-not-allowed' : ''"
                      class="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-2 mt-2"
                    >
                      <span v-if="isLocked">已锁定</span>
                      <span v-else-if="isLoggingIn">登录中...</span>
                      <span v-else>安全登录</span>
                      <ChevronRight :size="18" v-if="!isLoggingIn && !isLocked" />
                    </button>
                    
                    <!-- 其他登录方式入口 按需隐藏 -->
                    <div v-if="enableCodeLogin" class="w-full flex flex-col gap-3">
                       <div class="flex items-center gap-3 w-full">
                          <div class="h-px bg-white/5 flex-1"></div>
                          <span class="text-[10px] text-gray-600 font-bold uppercase tracking-widest">其他登录方式</span>
                          <div class="h-px bg-white/5 flex-1"></div>
                       </div>
                       <div class="flex gap-3">
                          <button @click="loginMethod = 'code'" class="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-bold text-gray-400 flex items-center justify-center gap-2 transition-all">
                             <ShieldCheck :size="14" /> 验证码登录
                          </button>
                       </div>
                    </div>
                  </div>
               </div>

               <div v-else-if="mode === 'register'" key="register" class="space-y-4">
                  <div class="space-y-1">
                    <div class="relative">
                      <UserIcon :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input v-model="registerData.username" type="text" placeholder="设置账号名称" @blur="checkUsername" :class="[errors.username ? 'border-red-500/50' : 'border-white/10']" class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" />
                    </div>
                    <p v-if="errors.username" class="text-[10px] text-red-500 pl-2">{{ errors.username }}</p>
                  </div>

                  <div class="space-y-1">
                    <div class="relative">
                      <Lock :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input v-model="registerData.password" type="password" placeholder="设置账号密码 (8位以上且含字母数字)" @blur="checkPassword" :class="[errors.password ? 'border-red-500/50' : 'border-white/10']" class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" />
                    </div>
                    <p v-if="errors.password" class="text-[10px] text-red-500 pl-2">{{ errors.password }}</p>
                  </div>

                  <div class="space-y-1">
                    <div class="relative">
                      <Mail :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input v-model="registerData.email" type="email" placeholder="绑定邮箱地址 (必填，用于找回密码)" @blur="checkEmail" :class="[errors.email ? 'border-red-500/50' : 'border-white/10']" class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" />
                    </div>
                    <p v-if="errors.email" class="text-[10px] text-red-500 pl-2">{{ errors.email }}</p>
                  </div>

                  <div class="space-y-1">
                    <div class="relative">
                      <Phone :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input v-model="registerData.phone" type="tel" placeholder="输入手机号码" @blur="checkPhone" :class="[errors.phone ? 'border-red-500/50' : 'border-white/10']" class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" />
                    </div>
                    <p v-if="errors.phone" class="text-[10px] text-red-500 pl-2">{{ errors.phone }}</p>
                  </div>

                  <div class="space-y-1">
                    <div class="relative">
                      <Ticket :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input v-model="registerData.invite_code" type="text" placeholder="邀请码 (可选)" class="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" />
                    </div>
                  </div>
                  <!-- 协议勾选 -->
                  <div class="space-y-1">
                    <label class="flex items-start gap-2 text-xs text-gray-400 cursor-pointer">
                      <input v-model="agreePolicy" type="checkbox" class="mt-0.5 accent-[#00d6b9]" />
                      <span>我已阅读并同意
                        <router-link to="/useragreement" class="text-[#00d6b9] hover:underline">《服务协议》</router-link>
                        和
                        <router-link to="/userprivacypolicy" class="text-[#00d6b9] hover:underline">《隐私协议》</router-link>
                      </span>
                    </label>
                  </div>
                  <button @click="handleRegister" class="w-full py-4 bg-gradient-to-r from-[#00d6b9] to-indigo-600 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-900/20 mt-4">
                     立即注册
                  </button>
               </div>

               <div v-else-if="mode === 'forgot'" key="forgot" class="space-y-6">
                  <div class="flex items-center gap-2 mb-2">
                     <button @click="mode = 'login'" class="p-2 -ml-2 text-gray-500 hover:text-white transition-colors">
                        <ArrowLeft :size="20" />
                     </button>
                     <h3 class="text-white text-xl font-bold">找回密码</h3>
                  </div>

                  <transition name="fade-scale" mode="out-in">
                    <div v-if="!recoverySent" key="form" class="space-y-4">
                      <p class="text-xs text-gray-400 leading-relaxed mb-4">我们将向您的注册邮箱发送一封包含重置链接的邮件。</p>
                      <div class="space-y-1">
                        <div class="relative">
                          <Mail :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                          <input v-model="forgotData.email" type="email" placeholder="输入注册邮箱地址" @blur="checkForgotEmail" :class="[errors.forgotEmail ? 'border-red-500/50' : 'border-white/10']" class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" />
                        </div>
                        <p v-if="errors.forgotEmail" class="text-[10px] text-red-500 pl-2">{{ errors.forgotEmail }}</p>
                      </div>
                      <button @click="handleForgot" class="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl transition-all shadow-xl shadow-indigo-900/20 mt-4 flex items-center justify-center gap-2">
                         <span>发送找回邮件</span> <ChevronRight :size="18" />
                      </button>
                    </div>
                    
                    <div v-else key="success" class="flex flex-col items-center py-8 text-center animate-fade-in">
                        <div class="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6 border border-green-500/30">
                           <CheckCircle2 :size="32" class="text-green-500" />
                        </div>
                        <h4 class="text-white font-bold text-lg mb-2">邮件已发送</h4>
                        <p class="text-gray-400 text-xs px-4">请检查您的收件箱。链接有效期为30分钟。即将返回登录页面...</p>
                    </div>
                  </transition>
               </div>

               <div v-else-if="mode === 'reset'" key="reset" class="space-y-6">
                  <div class="flex flex-col items-center mb-4">
                    <div class="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4 border border-indigo-500/30">
                       <ShieldCheck :size="24" class="text-indigo-400" />
                    </div>
                    <h3 class="text-white text-xl font-bold">设置新密码</h3>
                    <p class="text-xs text-gray-500 mt-2">请为您的账号设置一个安全的新密码</p>
                  </div>

                  <transition name="fade-scale" mode="out-in">
                    <div v-if="!resetSuccess" key="reset-form" class="space-y-4">
                      <div class="space-y-1">
                        <div class="relative">
                          <Lock :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                          <input 
                            v-model="resetData.new_password" 
                            :type="showPassword ? 'text' : 'password'" 
                            placeholder="新密码 (8位以上且含字母数字)" 
                            @blur="checkResetPassword"
                            :class="[errors.resetPassword ? 'border-red-500/50' : 'border-white/10']"
                            class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-10 text-xs focus:border-[#00d6b9] outline-none transition-colors" 
                          />
                          <button 
                            @click="showPassword = !showPassword" 
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                          >
                            <component :is="showPassword ? EyeOff : Eye" :size="14" />
                          </button>
                        </div>
                        <p v-if="errors.resetPassword" class="text-[10px] text-red-500 pl-2 animate-fade-in">{{ errors.resetPassword }}</p>
                      </div>

                      <div class="space-y-1">
                        <div class="relative">
                          <ShieldCheck :size="16" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                          <input 
                            v-model="resetData.re_new_password" 
                            type="password" 
                            placeholder="请再次输入新密码" 
                            @blur="checkResetRePassword"
                            :class="[errors.resetRePassword ? 'border-red-500/50' : 'border-white/10']"
                            class="w-full bg-white/5 border rounded-xl py-3 pl-10 pr-4 text-xs focus:border-[#00d6b9] outline-none transition-colors" 
                          />
                        </div>
                        <p v-if="errors.resetRePassword" class="text-[10px] text-red-500 pl-2 animate-fade-in">{{ errors.resetRePassword }}</p>
                      </div>

                      <button @click="handleResetPassword" class="w-full py-4 bg-[#00d6b9] hover:bg-[#00c4a8] text-[#0b0d10] font-bold rounded-2xl transition-all shadow-xl shadow-indigo-900/20 mt-4 flex items-center justify-center gap-2">
                         <span>立即重置密码</span> <ChevronRight :size="18" />
                      </button>
                      
                      <button @click="mode = 'login'" class="w-full py-2 text-xs text-gray-500 hover:text-gray-300 transition-colors">取消并返回登录</button>
                    </div>
                    
                    <div v-else key="reset-success" class="flex flex-col items-center py-8 text-center animate-fade-in">
                        <div class="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6 border border-green-500/30">
                           <CheckCircle2 :size="32" class="text-green-500" />
                        </div>
                        <h4 class="text-white font-bold text-lg mb-2">密码重置成功</h4>
                        <p class="text-gray-400 text-xs px-4">您的密码已成功更新。即将返回登录页面...</p>
                    </div>
                  </transition>
               </div>
             </transition>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

@keyframes scale-in {
  from { opacity: 0; transform: scale(0.9) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
.animate-scale-in { animation: scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateX(10px); }
.fade-slide-leave-to { opacity: 0; transform: translateX(-10px); }

@keyframes flow-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(10%, -10%) scale(1.1); }
  66% { transform: translate(-5%, 5%) scale(0.9); }
}
@keyframes flow-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-10%, 10%) scale(0.9); }
  66% { transform: translate(5%, -5%) scale(1.1); }
}
@keyframes flow-3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(0, -10%) scale(1.05); }
}

.animate-flow-1 { animation: flow-1 15s ease-in-out infinite; }
.animate-flow-2 { animation: flow-2 18s ease-in-out infinite; }
.animate-flow-3 { animation: flow-3 12s ease-in-out infinite; }

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }

input::placeholder { color: #4b5563; }

.fade-scale-enter-active, .fade-scale-leave-active { transition: all 0.3s ease; }
.fade-scale-enter-from { opacity: 0; transform: scale(0.95); }
.fade-scale-leave-to { opacity: 0; transform: scale(1.05); }

.truncate-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.25;
}
</style>